/** @format */

import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import {
	HeaderStoreSelection,
	ISkuLocation,
	ProductSku,
	Shipping,
	SKUResolveEvent
} from '@shared/data-access/interfaces';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { StoreService, UtilityService } from '@shared/data-access/services';
import {
	baseShippingInfoProcessed,
	getProductDataAction,
	inventoryFetched,
	inventoryFetchErrored,
	resolvedSkuShippingInfoProcessed,
	skuResolutionActions,
	skuResolutionChangedViaInventory
} from './product-selection.actions';
import {
	selectBopisInventory,
	selectDefiningAttributesLength,
	selectEcommInventory,
	selectIsResolvedSkuStorePickupAvailable,
	selectNearbyAvailableStoreCount,
	selectResolvedSku,
	selectSkusWithInventory
} from './product-selection.selectors';
import { selectUserStoreInfo, setNearbyStores } from '@state/data-access';
// Needs to be like this to prevent circular dependency
import { selectAtcUserInputQty } from '../../../../add-to-cart/data-access/src/lib/+state/add-to-cart.selectors';
import { concatLatestFrom } from '@ngrx/operators';
import { selectKillswitch } from './killswitches/killswitches.selectors';
import { selectSelectedAttributeOptionsMap } from '../../../../attributes/data-access/src/+state/attributes.selectors';
import { SkuResolutionService } from '../../../services/src/lib/product-selection/sku-resolution.service';

@Injectable()
export class ProductSelectionEffects {
	private readonly actions$ = inject(Actions);
	private readonly store = inject(Store);
	private readonly productAnalyticsService = inject(ProductAnalyticsService);
	private readonly utilityService = inject(UtilityService);
	private readonly storeService = inject(StoreService);

	private readonly stopWaitingAnalyticsEvents$ = new Subject<boolean>();

	fireResolveSkuAnalyticsEvent$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(...skuResolutionActions),
				filter(({ resolvedSku }) => !!resolvedSku),
				concatLatestFrom(() => [
					this.store.select(selectAtcUserInputQty),
					this.store.select(selectEcommInventory),
					this.store.select(selectBopisInventory),
					this.store.select(selectNearbyAvailableStoreCount),
					this.store.select(selectIsResolvedSkuStorePickupAvailable)
				]),
				// wait for store info to exist before firing
				switchMap((data) => {
					// prevent subscription from firing twice if resolved sku changes while waiting for store data
					this.stopWaitingAnalyticsEvents$.next(true);

					return this.store.select(selectUserStoreInfo).pipe(
						takeUntil(this.stopWaitingAnalyticsEvents$),
						filter((storeInfo) => storeInfo !== null),
						take(1),
						map((userSelectedStoreData) => ({ data, userSelectedStoreData }))
					);
				}),
				tap(
					({
						data: [
							{ resolvedSku },
							userInputQuantity,
							ecommInventory,
							bopisInventory,
							nearbyStoreCount,
							isSkuAvailableAtSelectedStore
						],
						userSelectedStoreData
					}) => {
						if (resolvedSku && userSelectedStoreData) {
							this.productAnalyticsService.triggerAnalyticsEvent(
								'SKUResolve',
								this.buildSkuResolveAnalyticsData(
									resolvedSku,
									userSelectedStoreData,
									userInputQuantity,
									ecommInventory,
									bopisInventory,
									nearbyStoreCount,
									isSkuAvailableAtSelectedStore
								)
							);
						}
					}
				)
			),
		{ dispatch: false }
	);

	buildSkuResolveAnalyticsData = (
		resolvedSku: ProductSku,
		userSelectedStoreData: HeaderStoreSelection,
		userInputQuantity: number,
		ecommInventory: ISkuLocation[],
		bopisInventory: ISkuLocation[],
		nearbyStoreCount: number,
		isSkuAvailableAtSelectedStore: boolean
	): SKUResolveEvent => {
		const availableToShipAtsQtyInventory = parseInt(
			this.storeService.getSkuInventoryFromLocations(ecommInventory, resolvedSku.partNumber)
				?.atsqty,
			10
		);

		const skuBopisInventory = this.storeService.getSkuInventoryFromLocations(
			bopisInventory,
			resolvedSku.partNumber
		);

		const bopisAtsQtyInventory = parseInt(skuBopisInventory?.atsqty, 10);
		const bopisIsaQtyInventory = parseInt(skuBopisInventory?.isaqty, 10);

		const isProductAvailableForPickup = this.isProductAvailableForPickup(
			resolvedSku,
			userInputQuantity,
			bopisAtsQtyInventory
		);
		const isProductAvailableInStore = this.isProductAvailableInStore(
			resolvedSku,
			userInputQuantity,
			bopisIsaQtyInventory
		);

		const isSkuBuyable = this.utilityService.isTruthy(resolvedSku?.buyable);

		const doesSkuHaveEcommInventory = availableToShipAtsQtyInventory > 0;

		const notifyUserWhenAvailableKillSwitchEnabled = this.store.selectSignal(
			selectKillswitch('notifyUserWhenAvailableEnabled')
		);

		// SET OBJECT FOR ANALYTICS EVENT DATA
		return {
			ecode: resolvedSku.parentPartNumber,
			sku: resolvedSku,
			AvailableForPickup: isProductAvailableForPickup,
			AvailableInStore: isProductAvailableInStore,
			AvailableToShip: availableToShipAtsQtyInventory > 0,
			QuantityRequested: userInputQuantity,
			lowStockShipMessageShown:
				!isNaN(availableToShipAtsQtyInventory) &&
				availableToShipAtsQtyInventory < 10 &&
				availableToShipAtsQtyInventory > 0 &&
				isSkuBuyable,
			lowStockShipQuantityAvailable: availableToShipAtsQtyInventory || 0,
			lowStockBopisMessageShown:
				!isNaN(bopisAtsQtyInventory) &&
				isProductAvailableInStore &&
				bopisAtsQtyInventory < 10 &&
				bopisAtsQtyInventory > 0 &&
				isSkuBuyable,
			lowStockBopisQuantityAvailable: bopisAtsQtyInventory || 0,
			lowStockNearbyStoresMessageShown:
				nearbyStoreCount > 0 && !isSkuAvailableAtSelectedStore,
			lowStockNearbyStoresCount: nearbyStoreCount,
			lowStockNearbyStoresCurrentStoreNumber:
				userSelectedStoreData.store || userSelectedStoreData.location,
			CurrentStoreNumber: this.utilityService.convertToNumber(
				userSelectedStoreData.store || userSelectedStoreData.location
			),
			notifyMeShown:
				notifyUserWhenAvailableKillSwitchEnabled() &&
				isSkuBuyable &&
				(!isProductAvailableInStore || !doesSkuHaveEcommInventory)
		};
	};

	/* Check for products' availability for in-store based on IsaQty */
	isProductAvailableInStore(
		resolvedSkuData: ProductSku,
		userInputQuantity: number,
		bopisIsaQtyInventory: number
	): boolean {
		return resolvedSkuData?.isaFlag && bopisIsaQtyInventory >= userInputQuantity;
	}

	/* Check for products' availability for in-store pickup based on atsQty and bopis */
	isProductAvailableForPickup(
		resolvedSkuData: ProductSku,
		userInputQuantity: number,
		bopisAtsQtyInventory: number
	): boolean {
		return resolvedSkuData?.bopisFlag && bopisAtsQtyInventory >= userInputQuantity;
	}

	// Set or unset resolved sku on inventory changes when all attributes are already selected
	updateResolvedSkuOnInventoryChange$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setNearbyStores, inventoryFetched, inventoryFetchErrored),
			concatLatestFrom(() => [
				this.store.select(selectSelectedAttributeOptionsMap),
				this.store.select(selectSkusWithInventory),
				this.store.select(selectDefiningAttributesLength),
				this.store.select(selectResolvedSku)
			]),
			filter(
				([, attributeOptionsMap, skusWithInventory, definingAttributesLength]) =>
					skusWithInventory.isDataInitialized &&
					Object.keys(attributeOptionsMap)?.length === definingAttributesLength
			),
			map(([, selectedAttributeOptionsMap, skusWithInventory]) => {
				// Resolve or unset the resolved sku based on the new inventory data
				const newResolvedSku = SkuResolutionService.getResolvedSku(
					skusWithInventory.skus,
					selectedAttributeOptionsMap
				);
				return skuResolutionChangedViaInventory({ resolvedSku: newResolvedSku });
			})
		)
	);

	processBaseShippingInfo$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getProductDataAction),
			filter(({ productSkus }) => !!productSkus && productSkus.length > 0),
			map(({ productSkus }) => {
				const shippingInfo = this.processShippingInfo(productSkus[0]);
				return baseShippingInfoProcessed({ shippingInfo });
			})
		)
	);

	processResolvedSKUShippingInfo$ = createEffect(() =>
		this.actions$.pipe(
			ofType(...skuResolutionActions),
			filter(({ resolvedSku }) => !!resolvedSku),
			map(({ resolvedSku }) => {
				const shippingInfo = this.processShippingInfo(resolvedSku);
				return resolvedSkuShippingInfoProcessed({ shippingInfo });
			})
		)
	);

	private readonly processShippingInfo = (productData: ProductSku) => {
		const shippingInfo: Shipping = {
			isPreSale: productData.preSale,
			isRestricted: productData.shippingRestricted,
			shippingClass: productData.shipClass,
			isHazmat: productData.isHazmat,
			leadTime: productData.leadTime
		};
		const shippingAttrLookup = {
			dsgHotMarketFlag: 'isHotMarket',
			PRESALE_END_DATE: 'preSaleDate',
			'Coming Soon End Date': 'comingSoon'
		};
		// loop through descriptive attributes and look for name matches
		productData.descriptiveAttributes.forEach((attribute) => {
			if (Object.prototype.hasOwnProperty.call(shippingAttrLookup, attribute.name)) {
				shippingInfo[shippingAttrLookup[attribute.name]] = attribute.value;
			}
		});

		return shippingInfo;
	};
}
